import { atom, useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import { userPermissionsAtom, userRolesAtom } from '@/atoms/auth';
import { plans } from '@/fixtures/plans';
import {
  checkHasAllPermissions,
  checkHasAnyPermission,
  checkHasAnyRole,
  checkHasPermission,
  checkHasRole,
} from '@/lib/auth/permissionUtils';

// Import the specific types
import type { Plan } from '@/fixtures/plans';
import type { Permission, PermissionChecker, Role } from '@/types/auth';

/**
 * Hook that checks if a user has a specific permission.
 *
 * Prefer this hook — and the other `useHas*Permission` variants — for any
 * feature-gating decision (can the user download 4K, access premium SFX,
 * etc.). Permissions can be granted individually (e.g. goodwill grants via
 * Retool) without upgrading the user's plan, and role-based checks silently
 * deny those users.
 *
 * Optimized to only re-render when the status of the specific permission changes.
 */
export function useHasPermission(permission?: Permission | null): boolean {
  const selectorAtom = useMemo(
    () =>
      selectAtom(
        userPermissionsAtom,
        (permissionsSet) => checkHasPermission(permissionsSet, permission),
        (a, b) => a === b
      ),
    [permission]
  );

  return useAtomValue(selectorAtom);
}

/**
 * Hook that checks if a user has a specific role.
 *
 * Roles describe plan tier (`Pro`, `Creator`, `Free`, etc.) — not capabilities.
 * Use this for plan-tier UX (pricing copy, lock badges, upsell destinations).
 * For gating a feature on whether the user can perform an action, use
 * `useHasPermission` instead — permissions can be granted individually
 * without a plan change.
 *
 * Optimized to only re-render when the status of the specific role changes.
 */
export function useHasRole(role?: Role | null): boolean {
  const selectorAtom = useMemo(
    () =>
      selectAtom(
        userRolesAtom,
        (rolesSet) => checkHasRole(rolesSet, role),
        (a, b) => a === b
      ),
    [role]
  );

  return useAtomValue(selectorAtom);
}

/**
 * Hook that returns all roles for the current user.
 * Defaults to returning a ReadonlySet.
 */
export function useRoles(): ReadonlySet<Role> {
  const rolesSet = useAtomValue(userRolesAtom);

  return rolesSet;
}

/**
 * Create a stable string key from an array of Permissions or Roles.
 * Sorts the array to ensure stability regardless of input order.
 */
export const getSetKey = (items?: readonly (Permission | Role)[]): string => {
  if (!items || items.length === 0) {
    return '';
  }

  // Sort a copy to avoid mutating the original array
  return [...items].sort().join(',');
};

/**
 * Hook that checks if a user has any of the specified permissions.
 */
export function useHasAnyPermission(
  permissions?: readonly Permission[]
): boolean {
  const permissionsKey = useMemo(() => getSetKey(permissions), [permissions]);

  const selectorAtom = useMemo(() => {
    // Optimization: If no permissions are requested, return false immediately.
    const requiredPermissions = permissions ?? [];

    if (requiredPermissions.length === 0) {
      // Return a constant atom to avoid unnecessary subscriptions
      return atom(false);
    }

    return selectAtom(
      userPermissionsAtom,
      (userPermissionsSet) =>
        checkHasAnyPermission(userPermissionsSet, requiredPermissions),
      (a, b) => a === b
    );
    // permissionsKey guarantees stability based on content
  }, [permissionsKey]);

  return useAtomValue(selectorAtom);
}

/**
 * Hook that checks if a user has all of the specified permissions.
 */
export function useHasAllPermissions(
  permissions?: readonly Permission[]
): boolean {
  const permissionsKey = useMemo(() => getSetKey(permissions), [permissions]);

  const selectorAtom = useMemo(() => {
    const requiredPermissions = permissions ?? [];

    // Optimization: If no permissions are requested, return true immediately.
    if (requiredPermissions.length === 0) {
      return atom(true);
    }

    return selectAtom(
      userPermissionsAtom,
      (userPermissionsSet) =>
        checkHasAllPermissions(userPermissionsSet, requiredPermissions),
      (a, b) => a === b
    );
    // permissionsKey guarantees stability based on content
  }, [permissionsKey]);

  return useAtomValue(selectorAtom);
}

/**
 * Hook for accessing and working with user permissions and roles.
 * Returns stable arrays and memoized helper functions.
 */
export function usePermissions(): PermissionChecker {
  const permissionsSet = useAtomValue(userPermissionsAtom);
  const rolesSet = useAtomValue(userRolesAtom);

  // Create a stable reference for permissions and roles arrays
  const permissions = useMemo(
    () => Array.from(permissionsSet),
    [permissionsSet]
  );

  const roles = useMemo(() => Array.from(rolesSet), [rolesSet]);

  // Create a stable isAuthenticated value
  const isAuthenticated = useMemo(
    () => permissions.length > 0 || roles.length > 0,
    [permissions.length, roles.length]
  );

  // Create callback functions with memoization dependent only on the Set references
  const hasPermission = useCallback(
    (permission?: Permission | null): boolean =>
      permission ? permissionsSet.has(permission) : false,
    [permissionsSet]
  );

  const hasRole = useCallback(
    (role?: Role | null): boolean => (role ? rolesSet.has(role) : false),
    [rolesSet]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions?: readonly Permission[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return false;
      }

      return requiredPermissions.some((p) => permissionsSet.has(p));
    },
    [permissionsSet]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions?: readonly Permission[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return true;
      }

      return requiredPermissions.every((p) => permissionsSet.has(p));
    },
    [permissionsSet]
  );

  // Return a memoized object implementing PermissionChecker interface
  // Optimize by only depending on stable references
  return useMemo(
    () => ({
      permissions,
      roles,
      isAuthenticated,
      hasPermission,
      hasRole,
      hasAnyPermission,
      hasAllPermissions,
      permissionsArray: permissions,
      rolesArray: roles,
    }),
    [
      permissions,
      roles,
      isAuthenticated,
      hasPermission,
      hasRole,
      hasAnyPermission,
      hasAllPermissions,
    ]
  );
}

/**
 * Hook that checks if a user has any of the specified roles.
 * Optimized to only re-render when the status for any of the roles changes.
 */
export function useHasAnyRole(roles?: readonly Role[]): boolean {
  const rolesKey = useMemo(() => getSetKey(roles), [roles]);

  const selectorAtom = useMemo(() => {
    const requiredRoles = roles ?? [];

    // Optimization: If no roles are requested, return false immediately.
    if (requiredRoles.length === 0) {
      return atom(false);
    }

    return selectAtom(
      userRolesAtom,
      (userRolesSet) => checkHasAnyRole(userRolesSet, requiredRoles),
      (a, b) => a === b
    );
    // rolesKey guarantees stability based on content
  }, [rolesKey]);

  return useAtomValue(selectorAtom);
}

/**
 * Hook that checks if the user is currently logged in.
 * This is determined by the presence of the 'User' role.
 */
export function useIsLoggedIn(): boolean {
  return useHasRole('User');
}

/**
 * Hook that checks if the user is an admin.
 * This is determined by the presence of the 'Admin' role.
 */
export function useIsAdmin(): boolean {
  return useHasRole('Admin');
}

/**
 * Hook to get the user's current subscription plan type based on their roles.
 * Defaults to 'Free' if no specific plan role is found.
 *
 * Use this for plan-tier UX — pricing copy, upsell destinations, "your plan
 * is X" labels. Do **not** use it to gate feature access: a user can be
 * granted individual permissions (e.g. via Retool) without their plan
 * changing, and a subscription-type check will silently lock them out. Gate
 * features on the specific permission via `useHasPermission` instead.
 */
export function useSubscriptionType(): Plan['name'] {
  const rolesSet = useRoles();

  // Memoize the result based on the rolesSet
  const subscriptionType = useMemo(() => {
    const foundPlan = plans.find((plan) =>
      // Check if the user has the role corresponding to the plan name
      // We cast to Role as plan names should align with Role types for plans
      rolesSet.has(plan.name as Role)
    );

    // Return the found plan's name or default to 'Free'
    return foundPlan?.name ?? 'Free';
  }, [rolesSet]);

  return subscriptionType;
}

const LIBRARY_URLS = {
  music: {
    root: '/music',
    searchQuery: 'music',
    contributorQuery: 'music',
  },
  sfx: {
    root: '/sfx',
    searchQuery: 'sfx',
    contributorQuery: 'sfx',
  },
  stockVideo: {
    root: '/stock-video',
    searchQuery: 'stock-video',
    contributorQuery: 'stock-video',
  },
  motionGraphics: {
    root: '/motion-graphics',
    searchQuery: 'motion-graphics',
    contributorQuery: 'motion-graphics',
  },
  luts: {
    root: '/luts',
    searchQuery: 'luts',
    contributorQuery: 'luts',
  },
};

const getTargetLibrary = (
  activePath: string,
  searchQuery: string | undefined
) => {
  const isSearch = activePath.startsWith('/search');
  const isContributor = activePath.startsWith('/contributors');

  if (
    activePath.startsWith(LIBRARY_URLS.music.root) ||
    (isSearch && searchQuery === LIBRARY_URLS.music.searchQuery) ||
    (isContributor && searchQuery === LIBRARY_URLS.music.contributorQuery)
  ) {
    return 'music';
  }
  if (
    activePath.startsWith(LIBRARY_URLS.sfx.root) ||
    (isSearch && searchQuery === LIBRARY_URLS.sfx.searchQuery) ||
    (isContributor && searchQuery === LIBRARY_URLS.sfx.contributorQuery)
  ) {
    return 'sfx';
  }
  if (
    activePath.startsWith(LIBRARY_URLS.stockVideo.root) ||
    (isSearch && searchQuery === LIBRARY_URLS.stockVideo.searchQuery) ||
    (isContributor && searchQuery === LIBRARY_URLS.stockVideo.contributorQuery)
  ) {
    return 'stock-video';
  }
  if (
    activePath.startsWith(LIBRARY_URLS.motionGraphics.root) ||
    (isSearch && searchQuery === LIBRARY_URLS.motionGraphics.searchQuery) ||
    (isContributor &&
      searchQuery === LIBRARY_URLS.motionGraphics.contributorQuery)
  ) {
    return 'motion-graphics';
  }
  if (
    activePath.startsWith(LIBRARY_URLS.luts.root) ||
    (isSearch && searchQuery === LIBRARY_URLS.luts.searchQuery) ||
    (isContributor && searchQuery === LIBRARY_URLS.luts.contributorQuery)
  ) {
    return 'luts';
  }

  return 'none';
};

/**
 * Hook to check what the correct homepage link is for the user.
 * Returns '/' if the user is logged out.
 * Returns the root of the current library if logged in.
 * Defaults to '/music' if user is on a non-library page.
 */
export function useHomepageLink(): string {
  const isLoggedIn = useIsLoggedIn();
  const router = useRouter();
  const activePath = router.isReady ? router.asPath : '';
  let searchQuery = router.isReady ? router.query.type : '';

  // If searchQuery is an array, get the first value
  if (Array.isArray(searchQuery)) {
    searchQuery = searchQuery.find((x) => x !== undefined);
  }

  const homepageLink = useMemo(() => {
    const targetLibrary = getTargetLibrary(activePath, searchQuery);
    if (!isLoggedIn) {
      // Take logged out users to the visitor home page
      return '/';
    }

    switch (targetLibrary) {
      case 'music':
        return LIBRARY_URLS.music.root;
      case 'sfx':
        return LIBRARY_URLS.sfx.root;
      case 'stock-video':
        return LIBRARY_URLS.stockVideo.root;
      case 'motion-graphics':
        return LIBRARY_URLS.motionGraphics.root;
      case 'luts':
        return LIBRARY_URLS.luts.root;
      default:
        // Take logged in users to music home page by default in all other cases
        return LIBRARY_URLS.music.root;
    }
  }, [isLoggedIn, activePath, searchQuery]);

  return homepageLink;
}
